/**
 * Permission catalogue. This file is the single source of truth: the seed step syncs it into
 * the `permissions` table and the API checks route access against these keys.
 *
 * Keys cover every planned module so that role definitions stay stable as modules ship.
 */
export const PERMISSIONS = {
  'organization:read': 'View organization profile',
  'organization:manage': 'Edit organization profile and settings',
  'members:read': 'View team members and invitations',
  'members:manage': 'Invite, remove and change roles of team members',
  'roles:manage': 'Create and edit custom roles',
  'audit:read': 'View the audit log',
  'billing:read': 'View subscription, usage and invoices',
  'billing:manage': 'Change plan, payment method and spending limits',
  'stores:read': 'View connected stores',
  'stores:manage': 'Connect, configure and disconnect stores',
  'api_keys:manage': 'Create, rotate and revoke API credentials',
  'customers:read': 'View customers and CRM data',
  'customers:manage': 'Edit customers, tags and notes',
  'orders:read': 'View orders and checkouts',
  'orders:manage': 'Mark orders paid and cancel them in the connected store',
  'automations:read': 'View automation flows and executions',
  'automations:manage': 'Create, edit and publish automation flows',
  'channels:read': 'View WhatsApp and Instagram connections',
  'channels:manage': 'Connect channels and manage templates',
  'inbox:read': 'Read conversations in the unified inbox',
  'inbox:reply': 'Reply to conversations',
  'inbox:assign': 'Assign conversations to team members',
  'campaigns:read': 'View campaigns',
  'campaigns:manage': 'Create, schedule and send campaigns',
  'ai:read': 'View AI agents and knowledge sources',
  'ai:manage': 'Configure AI agents and knowledge sources',
  'calls:read': 'View AI calls, transcripts and outcomes',
  'calls:manage': 'Configure and activate AI calling',
  'analytics:read': 'View analytics and revenue attribution',
  'privacy:manage': 'Handle data export and erasure requests',
} as const;

export type Permission = keyof typeof PERMISSIONS;

export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

export function isPermission(value: string): value is Permission {
  return Object.hasOwn(PERMISSIONS, value);
}

export const SYSTEM_ROLE_KEYS = ['owner', 'admin', 'manager', 'agent', 'viewer'] as const;
export type SystemRoleKey = (typeof SYSTEM_ROLE_KEYS)[number];

const READ_PERMISSIONS = ALL_PERMISSIONS.filter((p) => p.endsWith(':read'));

const MANAGER_EXCLUDED: Permission[] = [
  'organization:manage',
  'members:manage',
  'roles:manage',
  'billing:manage',
  'api_keys:manage',
  'privacy:manage',
];

export interface SystemRoleDefinition {
  /** Stable id so seeds are idempotent across environments. */
  id: string;
  key: SystemRoleKey;
  name: string;
  description: string;
  permissions: Permission[];
}

export const SYSTEM_ROLES: readonly SystemRoleDefinition[] = [
  {
    id: '00000000000000000000R0LE01',
    key: 'owner',
    name: 'Owner',
    description: 'Full access, including billing and ownership.',
    permissions: ALL_PERMISSIONS,
  },
  {
    id: '00000000000000000000R0LE02',
    key: 'admin',
    name: 'Admin',
    description: 'Full access except transferring ownership.',
    permissions: ALL_PERMISSIONS,
  },
  {
    id: '00000000000000000000R0LE03',
    key: 'manager',
    name: 'Manager',
    description: 'Runs day-to-day operations: automations, campaigns, inbox and CRM.',
    permissions: ALL_PERMISSIONS.filter((p) => !MANAGER_EXCLUDED.includes(p)),
  },
  {
    id: '00000000000000000000R0LE04',
    key: 'agent',
    name: 'Support agent',
    description: 'Handles customer conversations.',
    permissions: [
      'customers:read',
      'orders:read',
      'channels:read',
      'inbox:read',
      'inbox:reply',
      'ai:read',
      'calls:read',
    ],
  },
  {
    id: '00000000000000000000R0LE05',
    key: 'viewer',
    name: 'Viewer',
    description: 'Read-only access.',
    permissions: READ_PERMISSIONS,
  },
];

export function systemRole(key: SystemRoleKey): SystemRoleDefinition {
  const role = SYSTEM_ROLES.find((r) => r.key === key);
  if (!role) throw new Error(`Unknown system role ${key}`);
  return role;
}
